/**
 * Copyright 2019 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import type { ThunkAction } from '../types';
import azURL, { relPath } from '../config/serviceAPI';
import actionTypes from '../types/action';
import { deleteCookie } from '@/utils/cookie';
import { analyticsCookie } from '@/constants/analytics/analyticsCookie';
import { deletePreferredVehicleCookies } from '@/utils/analytics/dataLayer/preferredVehicleCookies';
import { type QueryClient } from '@tanstack/react-query';
import { localStorage } from '@/utils/localStorage';
import { HEADER_PRIMARY_KEY } from '@/features/header/api/getHeader';
import { COOKIES } from '@/constants/cookies';
import { dispatchEvent } from '@/utils/eventPublisher';
import { sessionStorage } from '@/utils/sessionStorage';
import { setGlobalStateQueryData } from '@/hooks/useGlobalState';

export const signout =
  (queryClient: QueryClient, onCompletion?: () => void): ThunkAction =>
  async (dispatch, _, axios) => {
    try {
      await axios.post(azURL(relPath.logOut), {
        headers: {
          'Content-Type': 'application/json;charset=UTF-8',
        },
      });
      localStorage.removeItem('rememberMeChecked');
      localStorage.removeItem('loggedInFrom');
      sessionStorage.removeItem('showOrderHistoryReviewCandidates');
      sessionStorage.removeItem('showHomeReviewCandidates');
      sessionStorage.removeItem('showAccountReviewCandidates');
      sessionStorage.removeItem('userPageViewAnalyticsCalled');

      await Promise.allSettled([
        deleteCookie(COOKIES.JSESSIONID),
        deleteCookie(COOKIES.AUTO_LOGIN),
        deleteCookie(analyticsCookie.cookieisSignedIn),
        deleteCookie(analyticsCookie.cookieHashedEId),
        deleteCookie(COOKIES.bearer_token),
      ]);
      deletePreferredVehicleCookies();
      await dispatch({ type: actionTypes.LOG_OUT });
      setGlobalStateQueryData('appData', { userAuthenticated: false }, queryClient);
      queryClient.removeQueries({ queryKey: ['user'] });
      queryClient.removeQueries({ queryKey: ['getWarranties'] });
      queryClient.removeQueries({ queryKey: ['myProfile'] });
      queryClient.removeQueries({ queryKey: ['myProfileV2'] });
      queryClient.removeQueries({ queryKey: ['currentUserData'] });
      queryClient.removeQueries({ queryKey: ['landingPage'] });
      queryClient.removeQueries({ queryKey: ['savedForLaterItems'] });
      queryClient.removeQueries({ queryKey: ['savedForLaterInfinite'] });
      queryClient.removeQueries({ queryKey: ['orderHistory'] });
      queryClient.removeQueries({ queryKey: ['reviewCandidates'] });
      await queryClient.refetchQueries({
        queryKey: [HEADER_PRIMARY_KEY],
      });
    } catch (err: unknown) {
      dispatch({
        type: actionTypes.SIGNOUT_FAILURE,
        errorLog: err,
      });

      return Promise.reject(new Error());
    } finally {
      onCompletion?.();
      dispatchEvent('signOut');
    }
  };
